import { type MarkdownDataMap } from '@atlassian/conversation-assistant-content-transformer/utils/stream-markdown-parser';
import {
	isStreamAnswerUiContent,
	isStreamAnswerUiPart,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { INLINE_LUMINA_COMPONENT_KEY, INLINE_LUMINA_PLACEHOLDER } from './inline-lumina-slots';

/**
 * Pure: returns placeholders and slot updates; the caller writes them to the store.
 *
 * rovo-chat-svc sends typed parts or legacy inline `◐…◑` markers in a response,
 * never both, so typed parts number their slots from 0 independently of the
 * streaming parser. A stream with both would put cards in the wrong slots, so it's
 * reported as `mixed_with_inline_markers`.
 */

export type AnswerUiPartsProtocolError =
	| 'malformed_part'
	| 'malformed_content'
	| 'duplicate_part'
	| 'duplicate_content'
	/** Count is the number of distinct parser `lumina-K` keys. */
	| 'mixed_with_inline_markers';

export type AnswerUiPartsCloseReason = 'final' | 'aborted';

export type AnswerUiSlotUpdate = {
	key: string;
	content: string;
	loading: boolean;
};

export type AnswerUiPartPlacement = {
	placeholder: string;
	markdownData: MarkdownDataMap;
};

export type AnswerUiPartsCloseResult = {
	reason: AnswerUiPartsCloseReason;
	partsWithoutPayload: number;
	/** Normal: tools can return cards the model never shows. */
	payloadsWithoutPart: number;
	/** Counted, so a misbehaving stream produces one report rather than one per event. */
	protocolErrors: Partial<Record<AnswerUiPartsProtocolError, number>>;
	/** Abort only: the final replace already overwrites all streamed state. */
	slotUpdates: AnswerUiSlotUpdate[];
};

type ContentRecord = { state: 'ready'; content: string } | { state: 'failed' };

export type AnswerUiPartsTracker = {
	onUiPart: (event: unknown) => AnswerUiPartPlacement | null;
	onUiContent: (event: unknown) => AnswerUiSlotUpdate | null;
	onParserMarkdownData: (markdownData: MarkdownDataMap) => void;
	close: (reason: AnswerUiPartsCloseReason) => AnswerUiPartsCloseResult | null;
};

const SLOT_KEY_PREFIX = `${INLINE_LUMINA_COMPONENT_KEY}-`;
const toSlotKey = (slot: number) => `${SLOT_KEY_PREFIX}${slot}`;
const isSlotKey = (key: string) => key.startsWith(SLOT_KEY_PREFIX);

const getSlotContent = (record: ContentRecord): string =>
	record.state === 'ready' ? record.content : '';

export const createAnswerUiPartsTracker = ({
	onEventAfterClose,
}: {
	/** Events after close can't be in the close result. Called at most once. */
	onEventAfterClose: () => void;
}): AnswerUiPartsTracker => {
	let nextSlot = 0;
	let closed = false;
	let hasReportedEventAfterClose = false;
	// Malformed typed events count too: they still mean RCS chose typed parts.
	let hasTypedEvents = false;
	// One key per legacy marker (a marker streamed over several chunks reuses its key),
	// recorded before any typed event too, since a legacy marker can arrive first.
	const parserLuminaKeys = new Set<string>();
	const slots = new Map<string, number>();
	// Kept after placement, so duplicate and close checks can use `content.has(partId)`.
	const content = new Map<string, ContentRecord>();
	const protocolErrors: Partial<Record<AnswerUiPartsProtocolError, number>> = {};

	const recordProtocolError = (error: AnswerUiPartsProtocolError) => {
		protocolErrors[error] = (protocolErrors[error] ?? 0) + 1;
	};

	const isAcceptingEvents = () => {
		if (!closed) {
			hasTypedEvents = true;
			return true;
		}
		if (!hasReportedEventAfterClose) {
			hasReportedEventAfterClose = true;
			onEventAfterClose();
		}
		return false;
	};

	const onUiPart: AnswerUiPartsTracker['onUiPart'] = (event) => {
		if (!isAcceptingEvents()) {
			return null;
		}
		if (!isStreamAnswerUiPart(event)) {
			recordProtocolError('malformed_part');
			return null;
		}
		const partId = event.part_id;
		if (slots.has(partId)) {
			recordProtocolError('duplicate_part');
			return null;
		}

		const slot = nextSlot++;
		const payload = content.get(partId);
		slots.set(partId, slot);

		return {
			placeholder: INLINE_LUMINA_PLACEHOLDER,
			markdownData: {
				[toSlotKey(slot)]: payload
					? { content: getSlotContent(payload), loading: false }
					: { content: '', loading: true },
			},
		};
	};

	const onUiContent: AnswerUiPartsTracker['onUiContent'] = (event) => {
		if (!isAcceptingEvents()) {
			return null;
		}
		if (!isStreamAnswerUiContent(event)) {
			recordProtocolError('malformed_content');
			return null;
		}
		const partId = event.part_id;
		if (content.has(partId)) {
			recordProtocolError('duplicate_content');
			return null;
		}

		const record: ContentRecord =
			event.state === 'ready'
				? { state: 'ready', content: event.message.content }
				: { state: 'failed' };
		content.set(partId, record);

		const slot = slots.get(partId);
		if (slot === undefined) {
			// Not an error: the payload waits until its part arrives.
			return null;
		}
		return {
			key: toSlotKey(slot),
			content: getSlotContent(record),
			loading: false,
		};
	};

	const onParserMarkdownData: AnswerUiPartsTracker['onParserMarkdownData'] = (markdownData) => {
		if (closed) {
			return;
		}
		for (const key in markdownData) {
			if (isSlotKey(key)) {
				parserLuminaKeys.add(key);
			}
		}
	};

	const close: AnswerUiPartsTracker['close'] = (reason) => {
		if (closed) {
			return null;
		}
		closed = true;

		if (hasTypedEvents && parserLuminaKeys.size > 0) {
			protocolErrors.mixed_with_inline_markers = parserLuminaKeys.size;
		}

		let partsWithoutPayload = 0;
		const slotUpdates: AnswerUiSlotUpdate[] = [];
		for (const [partId, slot] of slots) {
			if (content.has(partId)) {
				continue;
			}
			partsWithoutPayload += 1;
			if (reason === 'aborted') {
				slotUpdates.push({ key: toSlotKey(slot), content: '', loading: false });
			}
		}
		let payloadsWithoutPart = 0;
		for (const partId of content.keys()) {
			if (!slots.has(partId)) {
				payloadsWithoutPart += 1;
			}
		}

		slots.clear();
		content.clear();
		parserLuminaKeys.clear();

		return {
			reason,
			partsWithoutPayload,
			payloadsWithoutPart,
			protocolErrors: { ...protocolErrors },
			slotUpdates,
		};
	};

	return {
		onUiPart,
		onUiContent,
		onParserMarkdownData,
		close,
	};
};
